import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';

import { cn } from '../../lib/utils';

/**
 * 종이 — 아티클 상세와 커뮤니티 글 상세가 **같은 것**을 쓴다. 베이지 바탕·테두리·명조체(.article-paper 규칙)·
 * 우상단 글자 크기 −/+ 까지 하나다. 한쪽만 고치면 두 화면이 갈라진다.
 */
export function ArticlePaper({
  children,
  className,
  innerClassName,
}: {
  children: ReactNode;
  /** 바깥 상자에 더할 클래스 — 편집기는 flex 사슬과 카드 여백 상쇄(-mx)를 얹는다. */
  className?: string;
  /** 종이(.article-paper) 자체에 더할 클래스. */
  innerClassName?: string;
}) {
  const [scale, stepScale] = usePaperScale();
  return (
    <div className={cn('max-w-none leading-relaxed text-text', className)}>
      <div className={cn('article-paper', innerClassName)} style={{ '--paper-scale': scale } as CSSProperties}>
        <PaperControls scale={scale} onStep={stepScale} />
        {children}
      </div>
    </div>
  );
}

/* ------------------------------------------------ 글자 크기 +/− */

/** 단계. 1 이 기본(18px). 읽는 사람이 고른 값은 브라우저에 남는다. */
const PAPER_SCALES = [0.85, 1, 1.15, 1.3, 1.5] as const;
const PAPER_SCALE_KEY = 'zoxo_paper_scale';

export function usePaperScale(): [number, (step: -1 | 1) => void] {
  const [scale, setScaleState] = useState<number>(1);
  useEffect(() => {
    try {
      const saved = Number(window.localStorage.getItem(PAPER_SCALE_KEY));
      if (PAPER_SCALES.includes(saved as (typeof PAPER_SCALES)[number])) setScaleState(saved);
    } catch {
      /* 저장소가 막혀 있으면 기본값 */
    }
  }, []);
  // 함수형 갱신 — 빠르게 두 번 눌러도 한 단계씩 간다.
  const step = (dir: -1 | 1) =>
    setScaleState((prev) => {
      const i = PAPER_SCALES.indexOf(prev as (typeof PAPER_SCALES)[number]);
      const next = PAPER_SCALES[Math.min(PAPER_SCALES.length - 1, Math.max(0, (i < 0 ? 1 : i) + dir))]!;
      try {
        window.localStorage.setItem(PAPER_SCALE_KEY, String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  return [scale, step];
}

/** 종이 우상단의 −/+. */
export function PaperControls({ scale, onStep }: { scale: number; onStep: (step: -1 | 1) => void }) {
  const i = PAPER_SCALES.indexOf(scale as (typeof PAPER_SCALES)[number]);
  const idx = i < 0 ? 1 : i;
  return (
    <div className="article-paper__controls">
      <button
        type="button"
        className="article-paper__btn"
        aria-label="글자 작게"
        title="글자 작게"
        disabled={idx <= 0}
        onClick={() => onStep(-1)}
      >
        −
      </button>
      <button
        type="button"
        className="article-paper__btn"
        aria-label="글자 크게"
        title="글자 크게"
        disabled={idx >= PAPER_SCALES.length - 1}
        onClick={() => onStep(1)}
      >
        +
      </button>
    </div>
  );
}
